
import { useState, useEffect } from 'react';

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface TrackingStep {
  status: string;
  date: string;
  completed: boolean;
}

interface OrderData {
  orderNumber: string;
  status: string;
  orderDate: string;
  deliveryDate: string;
  items: OrderItem[];
  total: number;
  shippingAddress: string;
  trackingSteps: TrackingStep[];
}

export default function OrdersPage() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const heroSlides = [
    {
      title: "Premium Delivery Service",
      subtitle: "Your Mango Treasure pudding is carefully packaged and delivered fresh to your doorstep",
      image: "https://public.readdy.ai/ai/img_res/cec783b3dc1ab2122510cbbf2441ea5a.jpg"
    },
    {
      title: "Fresh & Safe Packaging",
      subtitle: "Temperature-controlled packaging ensures your pudding arrives in perfect condition",
      image: "https://readdy.ai/api/search-image?query=Professional%20food%20packaging%20facility%20with%20mango%20pudding%20being%20carefully%20packed%20in%20insulated%20boxes%2C%20quality%20control%20workers%20ensuring%20fresh%20delivery%2C%20modern%20packaging%20technology%2C%20food%20safety%20standards&width=1200&height=500&seq=2&orientation=landscape"
    },
    {
      title: "Real-Time Tracking",
      subtitle: "Track your order every step of the way from our facility to your home",
      image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20center%20with%20mango%20pudding%20orders%20being%20processed%20and%20tracked%2C%20delivery%20trucks%20with%20GPS%20tracking%2C%20professional%20warehouse%20operations%2C%20order%20fulfillment%20technology&width=1200&height=500&seq=3&orientation=landscape"
    },
    {
      title: "Customer Satisfaction",
      subtitle: "Join thousands of happy customers who trust us for premium mango pudding delivery",
      image: "https://readdy.ai/api/search-image?query=Happy%20customers%20receiving%20mango%20pudding%20delivery%20at%20their%20doorstep%2C%20satisfied%20families%20enjoying%20fresh%20delivered%20desserts%2C%20premium%20customer%20service%20experience%2C%20doorstep%20delivery%20success&width=1200&height=500&seq=4&orientation=landscape"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Mock order data for demonstration
  const mockOrders: Record<string, OrderData> = {
    'MT2024001': {
      orderNumber: 'MT2024001',
      status: 'delivered',
      orderDate: '2024-01-15',
      deliveryDate: '2024-01-18',
      items: [
        { name: 'Classic Mango Treasure Pudding', quantity: 2, price: 12.99 },
        { name: 'Mini Mango Cups (6-pack)', quantity: 1, price: 18.99 }
      ],
      total: 44.97,
      shippingAddress: '456 Oak Street, Hometown, HT 67890',
      trackingSteps: [
        { status: 'Order Placed', date: '2024-01-15 10:30 AM', completed: true },
        { status: 'Processing', date: '2024-01-15 2:15 PM', completed: true },
        { status: 'Shipped', date: '2024-01-16 9:00 AM', completed: true },
        { status: 'Out for Delivery', date: '2024-01-18 8:30 AM', completed: true },
        { status: 'Delivered', date: '2024-01-18 2:45 PM', completed: true }
      ]
    },
    'MT2024002': {
      orderNumber: 'MT2024002',
      status: 'shipped',
      orderDate: '2024-01-20',
      deliveryDate: '2024-01-23',
      items: [
        { name: 'Tropical Mango Delight', quantity: 3, price: 14.99 },
        { name: 'Mango Cream Parfait', quantity: 1, price: 16.99 }
      ],
      total: 61.96,
      shippingAddress: '789 Pine Avenue, Cityville, CV 12345',
      trackingSteps: [
        { status: 'Order Placed', date: '2024-01-20 11:15 AM', completed: true },
        { status: 'Processing', date: '2024-01-20 3:30 PM', completed: true },
        { status: 'Shipped', date: '2024-01-21 10:45 AM', completed: true },
        { status: 'Out for Delivery', date: '', completed: false },
        { status: 'Delivered', date: '', completed: false }
      ]
    },
    'MT2024003': {
      orderNumber: 'MT2024003',
      status: 'processing',
      orderDate: '2024-01-22',
      deliveryDate: '2024-01-25',
      items: [
        { name: 'Premium Mango Treasure Deluxe', quantity: 1, price: 22.99 },
        { name: 'Seasonal Mango Berry Fusion', quantity: 2, price: 15.99 }
      ],
      total: 54.97,
      shippingAddress: '321 Maple Drive, Townsburg, TB 54321',
      trackingSteps: [
        { status: 'Order Placed', date: '2024-01-22 9:20 AM', completed: true },
        { status: 'Processing', date: '2024-01-22 1:45 PM', completed: true },
        { status: 'Shipped', date: '', completed: false },
        { status: 'Out for Delivery', date: '', completed: false },
        { status: 'Delivered', date: '', completed: false }
      ]
    }
  };

  const handleTrackOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setOrderData(null);

    // Simulate API call delay
    setTimeout(() => {
      const order = mockOrders[trackingNumber];
      if (order) {
        setOrderData(order);
      } else {
        setError('Order not found. Please check your tracking number and try again.');
      }
      setIsLoading(false);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'shipped':
        return 'text-blue-600 bg-blue-100';
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'ri-check-double-line';
      case 'shipped':
        return 'ri-truck-line';
      case 'processing':
        return 'ri-time-line';
      default:
        return 'ri-package-line';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50">
      {/* Free Shipping Banner */}
      <section className="bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-truck-line text-2xl"></i>
            <span className="text-lg font-semibold">
              FREE SHIPPING on orders over $50!
            </span>
            <i className="ri-gift-line text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-rose-100/95 via-pink-100/95 to-orange-100/95 backdrop-blur-md shadow-xl sticky top-0 z-50 border-b border-rose-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                alt="Mango Treasure Logo"
                className="h-14 w-auto mr-4 drop-shadow-sm"
              />
              <span
                className="text-3xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Mango Treasure
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                >
                  Contact
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg flex items-center space-x-2 font-semibold"
                  href="/cart"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-lg shadow-lg transition-all duration-300 cursor-pointer border border-rose-100"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-xl border border-rose-200/50">
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <a
                  className="text-rose-700 hover:text-rose-900 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 cursor-pointer shadow-lg flex items-center space-x-2 font-semibold justify-center mt-4"
                  href="/cart"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Carousel */}
      <section className="relative h-96 overflow-hidden">
        <div className="relative w-full h-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                  <div className="text-white max-w-2xl">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                      {slide.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
                      {slide.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 group"
        >
          <i className="ri-arrow-left-line text-xl group-hover:scale-110 transition-transform"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 group"
        >
          <i className="ri-arrow-right-line text-xl group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div className="absolute top-6 right-6 bg-black/30 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
          {currentSlide + 1} / {heroSlides.length}
        </div>
      </section>

      {/* Order Tracking Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent mb-4">
              Track Your Order
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Enter your order number below to get real-time updates on your Mango Treasure delivery
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-rose-100">
            <form onSubmit={handleTrackOrder} className="space-y-6">
              <div>
                <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Order Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="trackingNumber"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter your order number (e.g., MT2024001)"
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-sm"
                    required
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Tracking...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-search-line mr-2"></i>
                    Track Order
                  </span>
                )}
              </button>
            </form>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-2"></i>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sample Order Numbers */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-2">Try these sample order numbers:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {Object.keys(mockOrders).map((orderNum) => (
                <button
                  key={orderNum}
                  onClick={() => setTrackingNumber(orderNum)}
                  className="px-3 py-1 bg-gray-100 hover:bg-rose-100 text-gray-700 hover:text-rose-700 rounded-full text-sm transition-colors duration-300"
                >
                  {orderNum}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Order Details */}
      {orderData && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50/50 via-pink-50/50 to-orange-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-rose-100">
              {/* Order Header */}
              <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 px-8 py-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">Order #{orderData.orderNumber}</h3>
                    <p className="text-rose-100">Placed on {new Date(orderData.orderDate).toLocaleDateString()}</p>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(orderData.status)}`}>
                    <i className={`${getStatusIcon(orderData.status)} mr-2`}></i>
                    {orderData.status.charAt(0).toUpperCase() + orderData.status.slice(1)}
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Tracking Progress */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-6">Tracking Progress</h4>
                  <div className="space-y-4">
                    {orderData.trackingSteps.map((step, index) => (
                      <div key={index} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                          step.completed 
                            ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' 
                            : 'bg-gray-200 text-gray-400'
                        }`}>
                          {step.completed ? (
                            <i className="ri-check-line text-sm"></i>
                          ) : (
                            <i className="ri-time-line text-sm"></i>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className={`font-medium ${step.completed ? 'text-gray-800' : 'text-gray-400'}`}>
                              {step.status}
                            </span>
                            <span className={`text-sm ${step.completed ? 'text-gray-600' : 'text-gray-400'}`}>
                              {step.date}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Order Items</h4>
                  <div className="space-y-3">
                    {orderData.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-800">{item.name}</span>
                          <span className="text-gray-600 ml-2">× {item.quantity}</span>
                        </div>
                        <span className="font-semibold text-gray-800">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-lg font-bold text-gray-800">
                      <span>Total</span>
                      <span>${orderData.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Shipping Address</h4>
                  <p className="text-gray-600">{orderData.shippingAddress}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Expected delivery: {new Date(orderData.deliveryDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Support */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-rose-100">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent mb-4">
              Need Help?
            </h3>
            <p className="text-gray-600 mb-6">
              If you have any questions about your order or need assistance, our customer support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-customer-service-line mr-2"></i>
                Contact Support
              </a>
              <a
                href="tel:(571) 531-8278"
                className="border-2 border-rose-300 hover:border-rose-500 text-rose-600 hover:text-rose-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/e97325bca996f1c8b8b8b8b8b8b8b8b8.png" 
                  alt="Mango Treasure" 
                  className="h-10 w-10 mr-3 rounded-full"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                  Mango Treasure
                </span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Delivering premium mango puddings crafted with love and the finest ingredients. 
                Experience the perfect blend of tradition and innovation in every bite.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-rose-400 transition-colors duration-300">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-rose-400 transition-colors duration-300">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-rose-400 transition-colors duration-300">
                  <i className="ri-twitter-line text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-rose-400">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="/" className="text-gray-300 hover:text-white transition-colors duration-300">Home</a></li>
                <li><a href="/products" className="text-gray-300 hover:text-white transition-colors duration-300">Products</a></li>
                <li><a href="/story" className="text-gray-300 hover:text-white transition-colors duration-300">Our Story</a></li>
                <li><a href="/contact" className="text-gray-300 hover:text-white transition-colors duration-300">Contact</a></li>
                <li><a href="/orders" className="text-gray-300 hover:text-white transition-colors duration-300">Track Orders</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-rose-400">Contact Info</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300">
                  <i className="ri-phone-line mr-2 text-rose-400"></i>
                  <a href="tel:(571) 531-8278" className="hover:text-white transition-colors duration-300">
                    (571) 531-8278
                  </a>
                </li>
                <li className="flex items-center text-gray-300">
                  <i className="ri-mail-line mr-2 text-rose-400"></i>
                  <a href="mailto:support@mangotreasure.com" className="hover:text-white transition-colors duration-300">
                    support@mangotreasure.com
                  </a>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-map-pin-line mr-2 text-rose-400 mt-1"></i>
                  <span>#303, 1952 Gallows Rd, Vienna, VA, United States</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 Mango Treasure. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              className="text-gray-400 hover:text-rose-400 text-sm transition-colors duration-300 mt-4 md:mt-0"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
